import { useMemo, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilterBar from "@/components/base/FilterBar";
import Pagination from "@/components/base/Pagination";
import StarCard from "@/components/feature/StarCard";
import EmptyState from "@/components/feature/EmptyState";
import { stars, starNationalities, starGenres } from "@/mocks/stars";
import { getStarFilms } from "@/lib/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 8;

export default function StarsCategory() {
  useDocumentMeta({
    title: "明星人物 - 巨乳娱乐资讯网",
    description:
      "巨乳娱乐资讯网明星人物栏目，聚合影视演员与主创资料，涵盖国籍、代表作品与主要作品类型，帮助你快速了解关注的明星。",
    keywords: "明星人物,演员资料,明星介绍,代表作品,巨乳娱乐资讯网",
  });

  const [gender, setGender] = useState("全部性别");
  const [nationality, setNationality] = useState("全部国籍");
  const [genre, setGenre] = useState("全部类型");
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () =>
      stars.filter((star) => {
        const genderOk = gender === "全部性别" || star.gender === gender;
        const nationOk =
          nationality === "全部国籍" || star.nationality === nationality;
        const genreOk =
          genre === "全部类型" ||
          getStarFilms(star).some((film) => film.genres.includes(genre));
        return genderOk && nationOk && genreOk;
      }),
    [gender, nationality, genre],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const handleFilter = (key: string, value: string) => {
    setPage(1);
    if (key === "gender") setGender(value);
    if (key === "nationality") setNationality(value);
    if (key === "genre") setGenre(value);
  };

  const resetAll = () => {
    setGender("全部性别");
    setNationality("全部国籍");
    setGenre("全部类型");
    setPage(1);
  };

  const groups = [
    { key: "gender", label: "性别", options: ["全部性别", "男", "女"], value: gender },
    {
      key: "nationality",
      label: "国籍",
      options: ["全部国籍", ...starNationalities],
      value: nationality,
    },
    {
      key: "genre",
      label: "作品类型",
      options: ["全部类型", ...starGenres],
      value: genre,
    },
  ];

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "明星人物" }]} />

      <div className="mt-4 flex flex-col gap-2">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          明星人物
        </h1>
        <p className="max-w-3xl text-sm leading-relaxed text-foreground-600">
          汇总演员与主创的基本资料、职业经历与代表作品。
          支持按性别、国籍与主要作品类型筛选，点击卡片即可查看完整的人物档案。
        </p>
      </div>

      <div className="mt-5">
        <FilterBar groups={groups} onChange={handleFilter} />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span className="text-sm text-foreground-500">
          共 <strong className="text-primary-600">{filtered.length}</strong> 位人物
        </span>
      </div>

      {pageItems.length > 0 ? (
        <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {pageItems.map((star) => (
            <StarCard key={star.id} star={star} />
          ))}
        </div>
      ) : (
        <div className="mt-5">
          <EmptyState
            title="没有符合条件的明星人物"
            description="试试更换筛选条件，或重置全部条件查看所有人物。"
            actionLabel="重置筛选条件"
            onAction={resetAll}
          />
        </div>
      )}

      <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}